/**
 * Resource that represents a Conveyor.
 *
 * @name Conveyor
 * @class
 * @augments TmrResource
 */
function ConveyorFactory(
  TmrApi,
  TmrResource,
  BarcodeReader,
  DeviceController,
  Place,
  RfidReader,
  User,
  tmrConveyorTypes,
  tmrConveyorModeTypes
) {
  'ngInject'
  let actions = {
    start: {
      method: 'POST',
      url: TmrApi.endpoint('Conveyor.start'),
    },
    stop: {
      method: 'POST',
      url: TmrApi.endpoint('Conveyor.stop'),
    },
  }
  let referencedProperties = [
    // conveyor.barcodeReader property will be instantiated as a BarcodeReader resource
    { property: 'barcodeReader', resource: BarcodeReader },
    // conveyor.deviceController property will be instantiated as a DeviceController resource
    { property: 'deviceController', resource: DeviceController },
    // conveyor.place property will be instantiated as a Place resource
    { property: 'place', resource: Place },
    // conveyor.rfidReader property will be instantiated as a RfidReader resource
    { property: 'rfidReader', resource: RfidReader },
    // conveyor.conveyorUser property will be instantiated as a User resource
    { property: 'conveyorUser', resource: User },
  ]
  let defaultValues = () => ({
    rfidAntennas: [],
  })
  let Conveyor = TmrResource(
    'Conveyor',
    actions,
    referencedProperties,
    defaultValues
  )

  Object.assign(Conveyor, {
    getTypeDescription,
    getModeTypeDescription,
    status,
  })
  Object.assign(Conveyor.prototype, {
    $getTypeDescription: getTypeDescription,
    $getModeTypeDescription: getModeTypeDescription,
    $status,
    $canDelete,
  })

  /**
   * Return Conveyor type description.
   *
   * @method Conveyor#getTypeDescription
   */
  function getTypeDescription() {
    return tmrConveyorTypes[this.type]
  }

  /**
   * Return Conveyor mode type description.
   *
   * @method Conveyor#getModeTypeDescription
   */
  function getModeTypeDescription() {
    return tmrConveyorModeTypes[this.conveyorMode]
  }

  /**
   * Gets the status of a conveyor
   *
   * @method Conveyor#status
   * @returns {string}
   */
  function status(id) {
    if (id === undefined)
      throw new Error('Conveyor.status(id) action requires a conveyor id')
    return TmrApi.get('Conveyor.status', { id }).then(({ data }) => data.status)
  }

  /**
   * Gets the status of this conveyor
   */
  function $status() {
    return status(this.id)
  }

  /**
   * Verifies if this conveyor can be deleted.
   *
   * @returns {Promise<string|boolean>} If can delete, value is true; if can't delete,
   * value is a translation string for the reason.
   */
  function $canDelete() {
    // Check if has a barcode reader.
    return Promise.resolve(
      this.barcodeReader && this.barcodeReader.id
        ? 'RESOURCEDELETE.CANTDELETE.CONVEYORBARCODE'
        : true
    )
  }

  return Conveyor
}

export const factory = ['Conveyor', ConveyorFactory]
